import { useEffect } from 'react'
import useStableObjectReference from './useStableObjectReference'
import noop from 'lib/function/noop'

function useArrowListNavigation(
  _listItemIds: Array<string>,
  options?: {
    enabled?: boolean
    onRequestClose?: () => void
    eventListenerTargetRef?: React.RefObject<HTMLElement | null>
    afterCloseFocusRef?: React.RefObject<HTMLElement | null>
  },
) {
  const listItemIds = useStableObjectReference(_listItemIds)
  const { enabled = true, onRequestClose = noop, eventListenerTargetRef, afterCloseFocusRef } = options ?? {}

  useEffect(() => {
    if (!enabled) return

    // Helper function to find next available element
    const findNextAvailableElement = (startIndex: number, step: number): string | null => {
      let index = startIndex
      const totalItems = listItemIds.length

      // Try each position until we find an existing and enabled element
      for (let i = 0; i < totalItems; i++) {
        index = (index + step + totalItems) % totalItems
        const element = document.getElementById(listItemIds[index])
        if (element) {
          const isDisabled =
            element.getAttribute('disabled') === '' ||
            element.getAttribute('disabled') === 'true'

          if (!isDisabled) {
            return listItemIds[index]
          }
        }
      }
      return null
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      const currentElement = document.activeElement
      const currentId = currentElement?.id
      const currentIndex = listItemIds.indexOf(currentId!)

      switch (event.key) {
        case 'ArrowDown': {
          event.preventDefault()
          const nextId = findNextAvailableElement(currentIndex, 1)
          if (nextId) document.getElementById(nextId)!.focus()
          break
        }
        case 'ArrowUp': {
          event.preventDefault()
          const prevId = findNextAvailableElement(currentIndex, -1)
          if (prevId) document.getElementById(prevId)!.focus()
          break
        }
        case 'Enter': {
          event.preventDefault()
          event.stopPropagation()
          ;(document.activeElement as HTMLElement)?.click()
          break
        }
        case 'Escape': {
          event.preventDefault()
          event.stopPropagation()
          onRequestClose()
          if (afterCloseFocusRef?.current) {
            afterCloseFocusRef.current.focus()
          }
          break
        }
      }
    }

    const eventListenerTarget = eventListenerTargetRef?.current ?? document

    eventListenerTarget.addEventListener('keydown', handleKeyDown)
    return () => eventListenerTarget.removeEventListener('keydown', handleKeyDown)
  }, [listItemIds, enabled, onRequestClose, eventListenerTargetRef, afterCloseFocusRef])
}

export default useArrowListNavigation
